/**
 * ContactSearchModal Component
 * Modal for searching and selecting contacts to add to a static list/segment.
 *
 * Search is EXPLICIT-SUBMIT (Search button or Enter) — it does not fire on open
 * and it does not fire per keystroke. Every submitted request carries a
 * monotonically increasing generation id and an AbortController; only the
 * newest generation may write results/loading/error. See RACE NOTE below.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../../services/supabase";
import { sanitizePostgrestSearchTerm } from "../../utils/postgrestSearch";
import type { Contact } from "../../types/contact.types";
import type { UUID } from "../../types/common.types";

interface ContactSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (contactIds: UUID[]) => Promise<void>;
  excludeContactIds?: UUID[]; // Contacts already in the list
  listName?: string;
}

/** Minimum trimmed characters before a search may be submitted. */
export const SEARCH_MIN_CHARS = 2;

const TOO_SHORT_MESSAGE = `Enter at least ${SEARCH_MIN_CHARS} characters to search.`;
const GENERIC_ERROR_MESSAGE =
  "Contact search failed. Please try again — no results are shown for this search.";

export default function ContactSearchModal({
  isOpen,
  onClose,
  onAdd,
  excludeContactIds = [],
  listName,
}: ContactSearchModalProps) {
  // What the user is typing right now.
  const [searchTerm, setSearchTerm] = useState("");
  // The trimmed term whose results are currently on screen / in flight. null
  // means "nothing has been submitted for the text now in the box", which is
  // what keeps stale rows from being presented as answers to new text.
  const [submittedTerm, setSubmittedTerm] = useState<string | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<UUID>>(new Set());
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // ---- RACE NOTE -----------------------------------------------------------
  // The previous implementation debounced a query per keystroke and rendered
  // whichever response resolved last. A slow earlier request could land after a
  // faster later one and overwrite the correct results (or flip loading=false
  // while a newer request was still running), so the user could see — and add —
  // contacts that did not match what they typed.
  //
  // Two independent guards now close that:
  //   1. `abortRef` aborts the in-flight request whenever a new one is
  //      submitted, the input is edited, or the modal closes/unmounts.
  //   2. `requestIdRef` is a monotonically increasing generation counter. Every
  //      request captures its id at issue time and may touch state ONLY if that
  //      id is still the latest. An aborted request that resolves anyway, or a
  //      slow response that beats its successor home, is dropped silently — it
  //      cannot write contacts, loading, or error.
  // Guard 2 is the one that actually makes staleness impossible; guard 1 just
  // stops useless work early.
  const requestIdRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  // Keep the latest exclusion set in a ref so the search callback can read it
  // without depending on the array's identity. Callers frequently pass a fresh
  // array literal (e.g. `excludeContactIds={[]}`), and depending on it would
  // rebuild the callback every render.
  const excludeKey = excludeContactIds.join(",");
  const excludeRef = useRef<UUID[]>(excludeContactIds);
  useEffect(() => {
    excludeRef.current = excludeContactIds;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excludeKey]);

  /**
   * Retire every outstanding request. Bumping the generation makes any response
   * still in flight unusable; aborting saves the round trip where possible.
   */
  const invalidateInFlight = useCallback(() => {
    requestIdRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  /** Drop everything on screen — used when the input changes or the modal closes. */
  const clearResults = useCallback(() => {
    setContacts([]);
    // Selection is always a subset of the results currently displayed, so it
    // goes with them. This is what makes it impossible to add a contact that
    // came from a search the user has moved on from.
    setSelectedIds(new Set());
    setSubmittedTerm(null);
    setSearchError(null);
    setLoading(false);
  }, []);

  const runSearch = useCallback(
    async (rawTerm: string) => {
      const trimmed = rawTerm.trim();

      if (trimmed.length < SEARCH_MIN_CHARS) {
        invalidateInFlight();
        setContacts([]);
        setSelectedIds(new Set());
        setSubmittedTerm(null);
        setLoading(false);
        setSearchError(TOO_SHORT_MESSAGE);
        return;
      }

      // Retire the previous request, then claim this generation.
      invalidateInFlight();
      const requestId = requestIdRef.current;
      const controller = new AbortController();
      abortRef.current = controller;

      // Show this search as the one in progress and drop the old rows up front.
      setContacts([]);
      setSelectedIds(new Set());
      setSearchError(null);
      setSubmittedTerm(trimmed);
      setLoading(true);

      const sanitized = sanitizePostgrestSearchTerm(trimmed);
      if (!sanitized) {
        // The term was entirely PostgREST-significant punctuation.
        if (requestId !== requestIdRef.current) {
          return;
        }
        setLoading(false);
        setSearchError(TOO_SHORT_MESSAGE);
        setSubmittedTerm(null);
        return;
      }

      try {
        let query = supabase
          .from("contacts")
          .select(
            "id, first_name, last_name, email, contact_type, phone_number",
          )
          .eq("is_archived", false)
          .or(
            `first_name.ilike.*${sanitized}*,last_name.ilike.*${sanitized}*,email.ilike.*${sanitized}*`,
          )
          .order("last_name", { ascending: true })
          .limit(50);

        const exclude = excludeRef.current;
        if (exclude.length > 0) {
          query = query.not("id", "in", `(${exclude.join(",")})`);
        }

        const { data, error } = await query.abortSignal(controller.signal);

        // Superseded (or the modal closed): this response is stale by
        // definition — do not touch any state, including `loading`.
        if (requestId !== requestIdRef.current) {
          return;
        }

        if (error) {
          console.error("Error searching contacts:", error);
          setContacts([]);
          setSelectedIds(new Set());
          setSearchError(GENERIC_ERROR_MESSAGE);
          setLoading(false);
          return;
        }

        setContacts((data as Contact[] | null) ?? []);
        setLoading(false);
      } catch (err) {
        if (requestId !== requestIdRef.current) {
          return;
        }
        console.error("Error searching contacts:", err);
        setContacts([]);
        setSelectedIds(new Set());
        setSearchError(GENERIC_ERROR_MESSAGE);
        setLoading(false);
      }
    },
    [invalidateInFlight],
  );

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    void runSearch(searchTerm);
  };

  const handleTermChange = (value: string) => {
    setSearchTerm(value);

    // Still exactly the term whose results are shown/loading — nothing to do.
    if (value.trim() === submittedTerm) {
      return;
    }
    // Nothing on screen and nothing in flight — nothing to invalidate.
    if (
      submittedTerm === null &&
      !loading &&
      searchError === null &&
      contacts.length === 0
    ) {
      return;
    }
    // The text no longer matches what produced these rows: retire both.
    invalidateInFlight();
    clearResults();
  };

  // Reset everything (and invalidate outstanding requests) when the modal
  // closes, so reopening can never render a response from the prior session.
  useEffect(() => {
    if (!isOpen) {
      invalidateInFlight();
      setSearchTerm("");
      setAddError(null);
      clearResults();
    }
  }, [isOpen, invalidateInFlight, clearResults]);

  // Same on unmount.
  useEffect(() => () => invalidateInFlight(), [invalidateInFlight]);

  const toggleSelect = (id: UUID) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === contacts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(contacts.map((c) => c.id)));
    }
  };

  const handleAdd = async () => {
    if (selectedIds.size === 0) {
      return;
    }

    setAdding(true);
    setAddError(null);
    try {
      await onAdd(Array.from(selectedIds));
      onClose();
    } catch (error) {
      console.error("Error adding contacts:", error);
      setAddError("Could not add the selected contacts. Please try again.");
    } finally {
      setAdding(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const trimmedTerm = searchTerm.trim();
  const canSubmit = trimmedTerm.length >= SEARCH_MIN_CHARS && !loading;

  // Which body the results pane shows. `idle` (submittedTerm === null) is the
  // state the modal opens in and returns to whenever the input is edited —
  // it is what guarantees rows are never displayed alongside text they do not
  // answer.
  const resultsState: "loading" | "error" | "idle" | "empty" | "rows" = (() => {
    if (loading) {
      return "loading";
    }
    if (searchError) {
      return "error";
    }
    if (submittedTerm === null) {
      return "idle";
    }
    return contacts.length === 0 ? "empty" : "rows";
  })();

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Add Contacts {listName && `to "${listName}"`}
            </h2>
            <button
              onClick={onClose}
              aria-label="Close"
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Search — explicit submit only (button or Enter). */}
          <div className="p-4 border-b border-gray-200">
            <form onSubmit={handleSubmit} className="flex gap-2">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => handleTermChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                placeholder="Search by name or email, then press Search"
                aria-label="Search contacts by name or email"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                autoFocus
              />
              <button
                type="submit"
                disabled={!canSubmit}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {loading ? "Searching..." : "Search"}
              </button>
            </form>
            <p className="mt-2 text-xs text-gray-500">
              Results appear only after you search, and always match the term
              you submitted.
            </p>
          </div>

          {/* Contact List */}
          <div className="max-h-96 overflow-y-auto">
            {resultsState === "loading" && (
              <div className="p-8 text-center text-gray-500">Searching...</div>
            )}
            {resultsState === "error" && (
              <div className="p-8 text-center">
                <p role="alert" className="text-sm text-red-600">
                  {searchError}
                </p>
                {searchError === GENERIC_ERROR_MESSAGE && (
                  <button
                    type="button"
                    onClick={() => handleSubmit()}
                    className="mt-3 px-3 py-1.5 text-sm font-medium text-primary-700 border border-primary-300 rounded-md hover:bg-primary-50"
                  >
                    Retry search
                  </button>
                )}
              </div>
            )}
            {resultsState === "idle" && (
              <div className="p-8 text-center text-gray-500">
                Enter at least {SEARCH_MIN_CHARS} characters and choose Search
                to find contacts.
              </div>
            )}
            {resultsState === "empty" && (
              <div className="p-8 text-center text-gray-500">
                No contacts found matching &ldquo;{submittedTerm}&rdquo;
              </div>
            )}
            {resultsState === "rows" && (
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        aria-label="Select all results"
                        checked={
                          selectedIds.size === contacts.length &&
                          contacts.length > 0
                        }
                        onChange={toggleSelectAll}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Type
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {contacts.map((contact) => (
                    <tr
                      key={contact.id}
                      className={`hover:bg-gray-50 cursor-pointer ${
                        selectedIds.has(contact.id) ? "bg-primary-50" : ""
                      }`}
                      onClick={() => toggleSelect(contact.id)}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(contact.id)}
                          onChange={() => toggleSelect(contact.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {contact.first_name} {contact.last_name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {contact.email || "-"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {contact.contact_types?.join(", ") ||
                          contact.contact_type ||
                          "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t border-gray-200">
            <span className="text-sm text-gray-500">
              {addError && (
                <span role="alert" className="text-red-600">
                  {addError}
                </span>
              )}
              {!addError &&
                (selectedIds.size > 0
                  ? `${selectedIds.size} contact${selectedIds.size !== 1 ? "s" : ""} selected`
                  : "Select contacts to add")}
            </span>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAdd}
                disabled={adding || selectedIds.size === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 flex items-center gap-2"
              >
                {adding ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Adding...
                  </>
                ) : (
                  `Add ${selectedIds.size > 0 ? selectedIds.size : ""} Contact${selectedIds.size !== 1 ? "s" : ""}`
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
